import { IoInformationCircleOutline } from 'react-icons/io5';

import { getEstimatorCopy } from '../copy';
import { IEstimatorApiResponse, IEstimatorReport } from '../types';

interface Props {
  deliveryState: IEstimatorApiResponse['delivery'] | null;
  language: 'en' | 'hr';
  report: IEstimatorReport;
}

const formatCurrency = (value: number, language: 'en' | 'hr') =>
  new Intl.NumberFormat(language === 'hr' ? 'hr-HR' : 'en-IE', {
    currency: 'EUR',
    maximumFractionDigits: 0,
    style: 'currency',
  }).format(value);

export const EstimatorReportCard: React.FC<Props> = ({ language, report }) => {
  const copy = getEstimatorCopy(language);
  const visibleRequirements = report.detectedRequirements.filter(
    item => item.label?.trim() && item.value?.trim(),
  );
  const estimateExplanation = copy.report.estimateExplanation;

  return (
    <section className="mt-16 rounded-[32px] border border-white/10 bg-white/[0.04] p-6 lg:p-8">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-sm font-semibold uppercase text-primary">
            {copy.report.reportIntro}
          </div>
          <h2 className="mt-3 text-2xl font-semibold text-white lg:text-3xl">
            {copy.report.reportHeading}
          </h2>
        </div>
        <div className="max-w-xl">{report.summary}</div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-5">
          <div className="text-sm uppercase text-primary">{copy.report.complexity}</div>
          <div className="mt-3 text-2xl font-semibold text-white">{report.complexityLabel}</div>
          <div className="mt-2 text-sm">Score: {report.complexityScore}</div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-5">
          <div className="text-sm uppercase text-primary">{copy.report.timeline}</div>
          <div className="mt-3 text-2xl font-semibold text-white">{report.timelineLabel}</div>
          <div className="mt-2 text-sm">{copy.report.timelineNote}</div>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm uppercase text-primary">{copy.report.investmentRange}</div>
            <div className="group relative">
              <button
                type="button"
                aria-label={copy.report.calculationDetails}
                className="text-white/45 transition-colors duration-300 hover:text-primary">
                <IoInformationCircleOutline size={18} />
              </button>
              <div className="pointer-events-none absolute right-0 top-7 z-20 hidden w-[280px] rounded-[18px] border border-white/10 bg-[#120B1C] p-4 text-sm text-white/78 shadow-2xl group-hover:block">
                {estimateExplanation}
              </div>
            </div>
          </div>
          <div className="mt-3 text-2xl font-semibold text-white">
            {formatCurrency(report.investmentRange.min, language)} -{' '}
            {formatCurrency(report.investmentRange.max, language)}
          </div>
          <div className="mt-2 text-sm">{copy.report.indicativeOnly}</div>
        </div>
      </div>

      <div className="mt-5 rounded-[24px] border border-white/10 bg-[#1C1027] p-5">
        <div className="text-sm font-semibold uppercase text-primary">
          {copy.report.calculationDetails}
        </div>
        <p className="mt-4 leading-7">{estimateExplanation}</p>
        <ul className="mt-4 space-y-1">
          {copy.report.estimateExplanationBullets.map(item => (
            <li key={item} className="flex items-center">
              <span className="h-px w-5 bg-primary mr-2" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-8 rounded-[24px] border border-primary/20 bg-primary/5 p-6">
        <div className="mb-3 text-sm font-semibold uppercase text-primary">
          {copy.report.fitHeading}
        </div>
        <p>{report.fitNote}</p>
      </div>

      {!!report.webshopDeliveryPath && (
        <div className="mt-8 rounded-[24px] border border-white/10 bg-[#1C1027] p-6">
          <div className="mb-4 text-sm font-semibold uppercase text-primary">
            {copy.report.webshopPathHeading}
          </div>
          <div className="rounded-[20px] border border-primary/20 bg-primary/5 p-5">
            <div className="text-sm uppercase text-primary">
              {copy.report.webshopPathRecommended}
            </div>
            <div className="mt-2 text-xl font-semibold text-white">
              {report.webshopDeliveryPath.label}
            </div>
            <p className="mt-3">{report.webshopDeliveryPath.rationale}</p>
          </div>
          <div className="mt-5 text-sm font-semibold uppercase text-primary">
            {copy.report.webshopPathAlternatives}
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            {report.webshopDeliveryPath.alternatives.map(item => (
              <div
                key={item.label}
                className="rounded-[20px] border border-white/10 bg-white/5 p-4">
                <div className="text-white font-semibold">{item.label}</div>
                <div className="mt-2 text-sm text-white/80">{item.summary}</div>
                <div className="mt-3 text-primary">
                  {formatCurrency(item.investmentRange.min, language)} -{' '}
                  {formatCurrency(item.investmentRange.max, language)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-6">
          <div className="mb-4 text-sm font-semibold uppercase text-primary">
            {copy.report.requirements}
          </div>
          <div className="flex flex-wrap gap-2">
            {visibleRequirements.map(item => (
              <span
                key={`${item.source}-${item.value}`}
                className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm">
                {item.label}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-6">
          <div className="mb-4 text-sm font-semibold uppercase text-primary">
            {copy.report.recommendedStack}
          </div>
          <ul className="space-y-2">
            {report.recommendedStack.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-6">
          <div className="mb-4 text-sm font-semibold uppercase text-primary">
            {copy.report.risks}
          </div>
          <ul className="space-y-2">
            {report.technicalRisks.length ? (
              report.technicalRisks.map(item => (
                <li key={item.label}>
                  <span className="text-white">{item.label}:</span> {item.reason}
                </li>
              ))
            ) : (
              <li>{copy.report.noMajorRisksFallback}</li>
            )}
          </ul>
        </div>

        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-6">
          <div className="mb-4 text-sm font-semibold uppercase text-primary">
            {copy.report.costSnapshot}
          </div>
          <ul className="space-y-2">
            {report.breakdown.slice(0, 6).map(item => (
              <li key={`${item.category}-${item.value}`} className="flex items-center">
                <span className="h-px w-5 bg-primary mr-2" />
                {item.label[0].toUpperCase() + item.label.slice(1)}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-[24px] border border-white/10 bg-[#1C1027] p-6">
          <div className="mb-4 text-sm font-semibold uppercase text-primary">
            {copy.report.nextSteps}
          </div>
          <ul className="space-y-2">
            {report.nextSteps.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
